/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useEffect, useState} from 'react';
import {ClassNameMap} from '@material-ui/core/styles/withStyles';
import {translate} from '@ion/core/src/locale/locale';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {Account, Applications, GenericObject} from '@ion/core/src/types/entities';
import {booleanToString} from '@ion/core/src/utils/BooleanUtils';
import {isObjectEmpty} from '@ion/core/src/utils/ObjectUtil';
import {FormItem} from '@ion/dls/src/components/forms/FormItem';
import {FormRadioGroup, Option as radioOptionsType} from '@ion/dls/src/components/forms/FormRadioGroup';
import {FormSection} from '@ion/dls/src/components/forms/FormSection';
import {FormTextField} from '@ion/dls/src/components/forms/FormTextField';
import Switch from '@ion/dls/src/components/switch/Switch';
import {InheritableOptions} from '@ion/dls/src/editors/controls/Inheritable';
import {GroupFormFields} from '@ion/dls/src/editors/GroupEditorUtils';
import {SFTPEditorEnablement} from '@ion/dls/src/editors/SFTPConfigurationEditor';
import TimezoneChooser from '@ion/dls/src/editors/TimezoneChooser';
import UnitOfMeasureChooser from '@ion/dls/src/editors/UnitOfMeasureChooser';
import {useMedia} from '@ion/dls/src/hooks/useMedia';
import {AccountApplicationsChooser} from '@ion/features/src/entities/account-applications/AccountApplicationsChooser';
import {useAccountDefaultApplications} from './hooks/useAccountDefaultApplications';
import AccountTooltip from './AccountTooltip';
import {AddAccountProps} from './AddGroupEditor';

export interface AccountSettingsProps extends AddAccountProps, SFTPEditorEnablement {
  handleApplicationChooserChange?: Function;
  handleRadioGroupChange?: Function;
  handleFieldChange?: Function;
  handleChooserChange?: Function;
  handleSwitchChange?: Function;
  setFormState?: Function;
  classes?: ClassNameMap<string>,
  group?: Account | GenericObject;
  account: Account;
  timeZone?: string;
  formState?: object;
  includeApplications?: boolean;
  isViewDialog?: boolean;
  isEditMode?: boolean;
  open?: boolean;
  isChipView?: boolean;
  displayApplicationName?: boolean;
  isLoggedinUserAccount?: boolean;
  hasCreateAppPermission?: boolean;
  hasCustomVisionPermission?: boolean;
};

/**
 * The AccountSettings component provides the UI which allows to add/edit
 *  fields such as their Applications, UnitOfMeasure, and TimeZone on
 *  account creation/updation.
 *
 *    import {AccountSettings} from '@ion/applications/src/pages/admin/accounts/AccountSettings';
 *
 *    <AccountSettings
 *       onChange={onChange} />
 *
 */
export const AccountSettings = ({
  handleApplicationChooserChange,
  handleRadioGroupChange,
  handleChooserChange,
  handleSwitchChange,
  handleFieldChange,
  setFormState,
  formState,
  account,
  classes,
  rootAccountId,
  group = {},
  timeZone = null,
  includeSFTPEditor = false,
  includeApplications = false,
  isViewDialog = false,
  isEditMode = false,
  open = false,
  isChipView = false,
  displayApplicationName,
  isLoggedinUserAccount = false,
  includeIonAccount = false,
  hasCreateAppPermission = false,
  hasCustomVisionPermission = false
}: AccountSettingsProps) => {
  const {isPhone}                               = useMedia();
  const [applicationsInfo, setApplicationsInfo] = useState<any>([]);
  const [applicationList, setApplicationList]   = useState<Applications>([]);

  const isEnterprise       = formState?.[GroupFormFields.Enterprise]?.value;
  const isViewMode         = isViewDialog || false;
  const isAddMode          = !isViewMode && !isEditMode;

  useEffect(() => {
    if (isEditMode) {
      setApplicationsInfo(group?.applications?.map(app => app.title));
    }
  }, [open, group]);

  const {
    applications,
    isLoading: isApplicationsLoading
  } = useAccountDefaultApplications({accountId: entityId(account)});

  /**
   * Retrieve the default applications for the selected Account
   * and show it as selected in the Application Drop down.
   */
  const setApplications = useCallback(() => {
    const {appNames, appHrefs} = applications || {};
    setApplicationsInfo(appNames);
    setFormState({
      [GroupFormFields.Applications] : {
        name     : GroupFormFields.Applications,
        value    : appHrefs,
        error    : Object.values(applications)?.length === 0,
        required : true
      }
    });
  }, [applications]);

  /**
   * Call the setApplications method only at that time of Account 
   * Creation.
   */
  useEffect(() => {
    if (!isApplicationsLoading && isAddMode) {
      setApplications();
    }
  }, [isApplicationsLoading, isAddMode]);

  useEffect(() => {
    if (!hasCreateAppPermission) {
      setFormState({
        [GroupFormFields.ApiAccess] : {
          name  : GroupFormFields.ApiAccess,
          value : false
        }
      });
    }
  }, [isEnterprise, hasCreateAppPermission]);

  const onApplicationChange = useCallback(value => {
    handleApplicationChooserChange?.({
      name     : GroupFormFields.Applications,
      error    : value?.length === 0,
      required : true,
      value
    });
  }, [handleApplicationChooserChange]);

  const handleAccountId = useCallback((): number => {
    if (!includeIonAccount || displayApplicationName) {
      return entityId(account);
    }
    return rootAccountId;
  }, [includeIonAccount, rootAccountId, displayApplicationName]);

  // It returns a new options with disabled = true for Yes value.
  const getDisabledOptions = useCallback((options: radioOptionsType[]) => {
    return options?.map((option) => ({
      ...option,
      disabled : option.label === booleanToString(true) ? true : option.disabled
    }));
  }, []);

  return (
    <FormSection bottomBorder={isEnterprise || includeSFTPEditor} title={translate('admin-groups:dialog.settings')}>
      <>
        {(isEnterprise && !isEditMode && !isLoggedinUserAccount) && (
          <FormItem fullWidth>
            <FormRadioGroup displayAsRow
              options={!hasCreateAppPermission ? getDisabledOptions(InheritableOptions) : InheritableOptions}
              name={GroupFormFields.ApiAccess}
              groupLabel={translate('admin-groups:dialog.fields.apiAccess.label')}
              defaultValue={formState[GroupFormFields.ApiAccess]?.value}
              onChange={handleRadioGroupChange}
              required />
            {formState[GroupFormFields.ApiAccess]?.error && (
              <div className={classes.errorMessage}>{translate('admin-groups:dialog.fields.apiAccess.error')}</div>
            )}
          </FormItem>
        )}
        {(includeApplications && !isLoggedinUserAccount) && (
          <FormItem fullWidth>
            <AccountApplicationsChooser required
              setApplicationList={setApplicationList}
              onChange={onApplicationChange}
              multiSelect
              accountId={handleAccountId()}
              includeNone={false}
              isChipView={isChipView}
              readOnly={isViewMode}
              hasError={formState[GroupFormFields.Applications]?.error}
              errorText={translate('common:features.applications.error')}
              defaultValue={applicationsInfo} />
          </FormItem>
        )}
        <FormItem>
          <UnitOfMeasureChooser onChange={function(value) {
            handleChooserChange({
              name : GroupFormFields.UnitOfMeasure,
              value
            });
          }} defaultValue={formState[GroupFormFields.UnitOfMeasure]?.value || 'Default'}
          isCtcAdmin
          readOnly={isViewMode} />
        </FormItem>
        <FormItem>
          <TimezoneChooser name={GroupFormFields.Timezone}
            includeNone
            noneOptionLabel={translate('timezones:fields.default')}
            readOnly={isViewMode}
            onChange={handleChooserChange}
            hasError={formState[GroupFormFields.Timezone]?.error}
            defaultValue={formState[GroupFormFields.Timezone]?.value || timeZone} />
        </FormItem>
        <FormItem>
          <div className={classes.flexStyle}>
            <div>
              <Switch label={translate('common:editors.autoAssetCreation.label')}
                marginRight={isPhone ? '25px' : '10px'}
                name={GroupFormFields.AutoAssetCreation}
                disabled={isViewMode || isLoggedinUserAccount}
                onChange={handleSwitchChange}
                checked={formState[GroupFormFields.AutoAssetCreation]?.value} />
            </div>
            <div className={classes.textTooltip}>
              <AccountTooltip title={translate('admin-groups:dialog.tooltips.assetCreationToolTip')} />
            </div>
          </div>
        </FormItem>
        <FormItem>
          <div className={classes.flexStyle}>
            <div>
              <Switch label={translate('common:editors.autoAssetAssociation.label')}
                marginRight={isPhone ? '25px' : '10px'}
                name={GroupFormFields.AutoAssetAssociation}
                disabled={isViewMode || isLoggedinUserAccount}
                onChange={handleSwitchChange}
                checked={formState[GroupFormFields.AutoAssetAssociation]?.value} />
            </div>
            <div className={classes.textTooltip}>
              <AccountTooltip title={translate('admin-groups:dialog.tooltips.assetAssociationToolTip')} />
            </div>
          </div>
        </FormItem>
        <FormItem>
          <div className={classes.flexStyle}>
            <div>
              <Switch label={translate('common:editors.customVision.label')}
                marginRight={isPhone ? '25px' : '10px'}
                name={GroupFormFields.UseCustomVisionSettings}
                disabled={isViewMode || isLoggedinUserAccount || !hasCustomVisionPermission}
                onChange={handleSwitchChange}
                checked={formState[GroupFormFields.UseCustomVisionSettings]?.value} />
            </div>
            <div className={classes.textTooltip}>
              <AccountTooltip title={translate('admin-groups:dialog.tooltips.customVisionToolTip')} />
            </div>
          </div>
        </FormItem>
        {isEditMode && (
          <FormItem fullWidth>
            <FormTextField disabled={isViewMode}
              fieldLabel={translate('common:editors.accountLabels.label')}
              placeholder={translate('common:editors.accountLabels.placeholder')}
              name={GroupFormFields.Labels}
              onChange={handleFieldChange}
              hasError={formState[GroupFormFields.Labels]?.error}
              defaultValue={formState[GroupFormFields.Labels]?.value}
              maxLength={64} />
          </FormItem>
        )}
      </>
    </FormSection>
  );
};

export default AccountSettings;
